import { createContext, useContext } from 'react';

/**
 * Platform-specific actions for the core experience.
 * Access-control actions (submitPin, submitPassword, etc.) are handled
 * by PublicAccessContext in the media-player app.
 */
export interface PlatformActions {
  /** Get auth credentials (bearer token + password) from cookies */
  getAuthCredentials: (params: {
    projectId?: string;
    publicationId?: string;
    organizationSlug?: string;
    instanceSlug?: string;
    cookiePrefix?: string;
  }) => Promise<{ bearerToken?: string; password?: string; documentIds?: string[] }>;
}

export interface ExperienceConfig {
  /** API root URL (e.g., https://api.dev.libertify.com) */
  apiRootUrl: string;
  /** Chatbot URL */
  chatbotUrl: string;
  /** Lead form URL */
  leadFormUrl?: string;
  /** Download form URL */
  downloadFormUrl?: string;
  /** Segment analytics write key */
  segmentWriteKey?: string;
  /** App prefix for cookies/storage (e.g., 'libertify' or 'docustream') */
  appPrefix: string;
}

export interface ExperienceContextValue {
  actions: PlatformActions;
  config: ExperienceConfig;
}

const ExperienceContext = createContext<ExperienceContextValue | null>(null);

export { ExperienceContext };
export const ExperienceProvider = ExperienceContext.Provider;

export function useExperienceContext(): ExperienceContextValue {
  const ctx = useContext(ExperienceContext);
  if (!ctx) {
    throw new Error(
      'useExperienceContext must be used within an ExperienceProvider',
    );
  }
  return ctx;
}

export function useExperienceActions(): PlatformActions {
  return useExperienceContext().actions;
}

export function useExperienceConfig(): ExperienceConfig {
  return useExperienceContext().config;
}
